import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSyncRefs } from "@shared/hooks/useSyncRef";
import { scrollToElementById } from "@utils/ui";
import classNames from "classnames";

import { useLayoutContext } from "@context/layout";
import { ADMIN_HEADER_HEIGHT } from "./helpers/constants";
import { useIsMobile } from "@shared/hooks/useIsMobile";

export type HeaderKey =
  | "features"
  | "preview"
  | "content"
  | "about"
  | "prerequisite"
  | "warranty"
  | "comments"
  | "faqs"
  | "aboutInstructor";

// eslint-disable-next-line no-unused-vars
export type HeaderItem<T extends HeaderKey = HeaderKey> = {
  id: string;
  offset: number;
  showHeader: boolean;
  enable: boolean;
  title: string;
};
export type HeaderItems = {
  [key in HeaderKey]: HeaderItem<key>;
};

interface Props {
  activeElement: string;
  items: HeaderItems;
  siteHeaderOffset: number;
  headerHeight: number;
  /** Sticky stack height */
  scrollOffset?: number;
}
const Header = React.forwardRef<HTMLElement, Props>(
  (
    { activeElement, items, siteHeaderOffset, headerHeight, scrollOffset },
    ref
  ) => {
    const { isAdmin } = useLayoutContext();
    const [activeTab, setActiveTab] = useState("");
    const tabRefs = useRef<Record<string, HTMLAnchorElement | null>>({});

    useEffect(() => {
      if (activeElement) setActiveTab(activeElement);
    }, [activeElement]);

    const headerRef = useRef<HTMLHeadingElement | null>(null);
    const _ref = useSyncRefs(ref, headerRef);
    const isMobile = useIsMobile(768);

    useEffect(() => {
      if (!activeTab) return;
      const activeEl = tabRefs.current[activeTab];
      if (!activeEl) return;

      activeEl.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }, [activeTab]);

    const offsetTop = useMemo(() => {
      const compact = isMobile
        ? isAdmin
          ? ADMIN_HEADER_HEIGHT
          : 0
        : headerHeight;
      return siteHeaderOffset + compact - 2 + "px";
    }, [siteHeaderOffset, headerHeight, isMobile, isAdmin]);

    const onClick = (
      e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
      item: HeaderItem
    ) => {
      e.preventDefault();
      setActiveTab(item.id);
      scrollToElementById(item.id, scrollOffset ?? item.offset);
    };

    return (
      <header
        ref={_ref}
        className="sticky pt-1.5  md:pt-2  overflow-x-auto hide-scroll z-[42]  md:z-30 w-full border-b border-b-fds-cart bg-fds-primary transition-all duration-150 ease-linear"
        style={{
          top: offsetTop,
        }}
      >
        <div className="mx-auto section-container px-4     xl:px-0">
          <div className="flex gap-2  md:gap-4    ">
            {Object.keys(items).map((m) => {
              const item = items[m as HeaderKey];
              const isActive = activeTab === item.id;
              return (
                item.enable &&
                item.showHeader && (
                  <a
                    key={item.id}
                    ref={(el) => {
                      tabRefs.current[item.id] = el;
                    }}
                    className={classNames(
                      "cursor-pointer md:hover:bg-fds-secondary select-none md:select-auto whitespace-nowrap flex items-center text-center bg-fds-primary min-h-12 md:min-h-fds-50",
                      "py-3  px-2.5   relative",
                      isActive
                        ? " text-fds-brand font-medium"
                        : "cursor-pointer "
                    )}
                    href={`#${item.id}`}
                    onClick={(e) => onClick(e, item)}
                  >
                    <p className="title-sm md:title-md-medium">{item.title}</p>
                    {isActive && (
                      <div className="w-full rounded-t-2 absolute bottom-0 right-0 h-0.5 bg-fds-brand z-50" />
                    )}
                  </a>
                )
              );
            })}
          </div>
        </div>
      </header>
    );
  }
);
Header.displayName = "Header";

export default React.memo(Header);
